/**
 * 2026-07-02 (Leon 反馈) — mic permission helper.
 *
 * Any getUserMedia call site can `classifyMicError(e)` to tell whether
 * the failure was a permission problem vs a device/hardware problem
 * vs something else. On permission failure, dispatch the global
 * `mic-permission-denied` event so <MicPermissionModal /> mounted at
 * the root layout can surface an actionable modal (with platform-
 * specific instructions on how to unblock).
 *
 * NOTE: web pages CANNOT programmatically open `chrome://settings/*`
 * — that's a browser security invariant, no workaround. Best UX is
 * a modal with clear steps + a 重试 button that re-calls getUserMedia.
 */

export type MicErrorKind =
  | 'permission_denied' // NotAllowedError, PermissionDeniedError
  | 'no_device' // NotFoundError, DevicesNotFoundError
  | 'busy' // NotReadableError, TrackStartError (another app is using it)
  | 'insecure_context' // page not on HTTPS
  | 'unknown';

export type Platform = 'android_chrome' | 'ios_safari' | 'ios_other' | 'desktop';

export function classifyMicError(e: unknown): MicErrorKind {
  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return 'insecure_context';
  }
  if (e instanceof DOMException) {
    if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
      return 'permission_denied';
    }
    if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
      return 'no_device';
    }
    if (e.name === 'NotReadableError' || e.name === 'TrackStartError') {
      return 'busy';
    }
  }
  const msg = e instanceof Error ? e.message.toLowerCase() : '';
  if (msg.includes('permission') || msg.includes('denied') || msg.includes('not allowed')) {
    return 'permission_denied';
  }
  return 'unknown';
}

export function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPad|iPhone|iPod/i.test(ua);
  const isChrome = /Chrome|Chromium|CriOS/i.test(ua);
  if (isAndroid && isChrome) return 'android_chrome';
  if (isIOS && /Safari/i.test(ua) && !/CriOS|FxiOS/i.test(ua)) return 'ios_safari';
  if (isIOS) return 'ios_other';
  return 'desktop';
}

export type MicPermissionDeniedDetail = {
  kind: MicErrorKind;
  platform: Platform;
  /** Original error message if available — useful for support triage. */
  raw?: string;
};

const EVENT_NAME = 'mic-permission-denied';

/** Fire the global event so <MicPermissionModal /> opens. */
export function reportMicError(e: unknown): void {
  if (typeof window === 'undefined') return;
  const kind = classifyMicError(e);
  const platform = detectPlatform();
  const raw = e instanceof Error ? e.message : String(e ?? '');
  window.dispatchEvent(
    new CustomEvent<MicPermissionDeniedDetail>(EVENT_NAME, {
      detail: { kind, platform, raw },
    }),
  );
}

export function subscribeMicError(
  handler: (detail: MicPermissionDeniedDetail) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => {
    const ce = e as CustomEvent<MicPermissionDeniedDetail>;
    handler(ce.detail);
  };
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}

/**
 * Query the persistent permission state. Returns 'granted' / 'denied' /
 * 'prompt' / 'unsupported'. When 'denied', calling getUserMedia will
 * throw without prompting the user — the only recovery is via browser
 * settings (which we can't open programmatically).
 */
export async function queryMicPermission(): Promise<
  'granted' | 'denied' | 'prompt' | 'unsupported'
> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return 'unsupported';
  }
  try {
    // TypeScript's PermissionName doesn't include 'microphone' in every
    // lib version even though every modern browser accepts it. Cast to
    // silence the check.
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    return status.state as 'granted' | 'denied' | 'prompt';
  } catch {
    return 'unsupported';
  }
}
